import type { GuitarChart } from '@shared/guitarChart';
import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import { withAlpha, comboTier } from '../../piano/render/pianoRenderColour';
import {
  BEAT_PULSE_STRENGTH,
  COMBO_BG_TIER_THRESHOLD,
  COMBO_BLOOM_PALETTE,
  LANE_GAP_PX,
  LANE_GEM_ACCENTS,
  LANE_GEM_COLOURS,
} from './guitarRenderConstants';
import { computeLayout, type GuitarLayout } from '../guitarPlayfieldGeometry';
import type { PianoLaneFlash } from '../../piano/render/pianoRenderTypes';
import type { RenderQuality } from '../../rendering/renderQuality';
import { beatPulse } from './guitarSceneAtmosphere';

/**
 * Scene chrome for Guitar mode — background bloom, the perspective
 * trapezoid highway, lane stripes, the strum bar with fret caps +
 * key labels, and lane verdict flashes spawned by the FX system.
 *
 * Caches every gradient it builds (background bloom, per-lane stripe
 * fills, per-lane pressed-wash fills) keyed on the dimensions that
 * drive each gradient — would otherwise fire 8+ `createLinearGradient`
 * calls per frame on a busy chart. Mirrors the {@link
 * import('../../piano/render/pianoDrawScene.js').PianoSceneRenderer}
 * caching pattern.
 */
export class GuitarSceneRenderer {
  private bgBloomGradient: CanvasGradient | null = null;
  private bgBloomKey = '';
  /**
   * One stripe + one press-wash gradient per lane. Lane colours are
   * constant ({@link LANE_GEM_COLOURS} / {@link LANE_GEM_ACCENTS}),
   * so both sets only change on canvas resize. Cache key is the
   * (highwayTopY, strumBarY) pair — see {@link ensureLaneGradients}.
   */
  private laneStripeGradients: (CanvasGradient | null)[] = [null, null, null, null];
  private pressWashGradients: (CanvasGradient | null)[] = [null, null, null, null];
  private laneGradientKey = '';

  /**
   * Background fill plus a subtle radial bloom at the bottom that
   * shifts hue / intensity with combo tier. Mirrors Piano's bloom
   * cadence so a player who switches between modes gets the same
   * "the music is getting hyped" reading on long combos.
   */
  drawBackground(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    palette: GuitarChart['palette'],
    combo: number,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    ctx.fillStyle = palette.bg;
    ctx.fillRect(0, 0, width, height);
    if (reducedMotion || !quality.backgroundBloom) {
      return;
    }
    const tier = comboTier(combo);
    const bloomColour =
      combo >= COMBO_BG_TIER_THRESHOLD ? COMBO_BLOOM_PALETTE[tier] : palette.pad;
    const bloomAlpha = 0.08 + Math.min(0.18, combo / 200);
    const key = `${width}|${height}|${bloomColour}`;
    if (!this.bgBloomGradient || this.bgBloomKey !== key) {
      const gradient = ctx.createRadialGradient(
        width / 2,
        height,
        0,
        width / 2,
        height,
        Math.max(width, height) * 0.9,
      );
      gradient.addColorStop(0, withAlpha(bloomColour, 1));
      gradient.addColorStop(1, withAlpha(bloomColour, 0));
      this.bgBloomGradient = gradient;
      this.bgBloomKey = key;
    }
    ctx.save();
    ctx.globalAlpha = bloomAlpha;
    ctx.fillStyle = this.bgBloomGradient;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }

  /**
   * The perspective fretboard "highway" — a trapezoid (wide at the
   * strum bar, narrow at the back-wall horizon) with palette-tinted
   * lane stripes plus a warm wash on each currently-pressed lane.
   * Borders are the chart's `bgAccent` so the highway reads cleanly
   * on the background gradient. Composite pass order:
   *   1. Trapezoid background (palette.bgAccent)
   *   2. Per-lane vertical-stripe gradient (palette-tinted)
   *   3. Pressed-lane overlay (warm white wash)
   */
  drawHighway(
    ctx: CanvasRenderingContext2D,
    layout: GuitarLayout,
    palette: GuitarChart['palette'],
    pressedLanes: ReadonlySet<ManiaLane>,
  ): void {
    // Outer trapezoid — same shape as the lane stack but extended a
    // few px on each side for a clear silhouette against the bg.
    const margin = 6;
    const topLeft = layout.centreX - layout.topStackWidth / 2 - margin;
    const topRight = layout.centreX + layout.topStackWidth / 2 + margin;
    const bottomLeft = layout.centreX - layout.bottomStackWidth / 2 - margin;
    const bottomRight = layout.centreX + layout.bottomStackWidth / 2 + margin;
    ctx.fillStyle = palette.bgAccent;
    ctx.beginPath();
    ctx.moveTo(topLeft, layout.highwayTopY);
    ctx.lineTo(topRight, layout.highwayTopY);
    ctx.lineTo(bottomRight, layout.strumBarY);
    ctx.lineTo(bottomLeft, layout.strumBarY);
    ctx.closePath();
    ctx.fill();

    this.ensureLaneGradients(ctx, layout);

    // Lane fills — each is a quadrilateral whose top + bottom widths
    // come from the layout's `topLaneWidth` / `bottomLaneWidth`. We
    // walk left → right and place each lane at the correct lateral
    // offset for both the top and bottom edges.
    const topGap = LANE_GAP_PX * (layout.topStackWidth / layout.bottomStackWidth);
    const topStackLeft = layout.centreX - layout.topStackWidth / 2;
    const bottomStackLeft = layout.centreX - layout.bottomStackWidth / 2;
    for (let lane = 0; lane < MANIA_LANE_COUNT; lane += 1) {
      const tlX = topStackLeft + lane * (layout.topLaneWidth + topGap);
      const trX = tlX + layout.topLaneWidth;
      const blX = bottomStackLeft + lane * (layout.bottomLaneWidth + LANE_GAP_PX);
      const brX = blX + layout.bottomLaneWidth;
      ctx.fillStyle = this.laneStripeGradients[lane] as CanvasGradient;
      ctx.beginPath();
      ctx.moveTo(tlX, layout.highwayTopY);
      ctx.lineTo(trX, layout.highwayTopY);
      ctx.lineTo(brX, layout.strumBarY);
      ctx.lineTo(blX, layout.strumBarY);
      ctx.closePath();
      ctx.fill();

      if (pressedLanes.has(lane as ManiaLane)) {
        ctx.fillStyle = this.pressWashGradients[lane] as CanvasGradient;
        ctx.beginPath();
        ctx.moveTo(tlX, layout.highwayTopY);
        ctx.lineTo(trX, layout.highwayTopY);
        ctx.lineTo(brX, layout.strumBarY);
        ctx.lineTo(blX, layout.strumBarY);
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  /** Rebuild the per-lane gradient pair when the highway y-span changes (resize). */
  private ensureLaneGradients(
    ctx: CanvasRenderingContext2D,
    layout: GuitarLayout,
  ): void {
    const key = `${layout.highwayTopY}|${layout.strumBarY}`;
    if (this.laneGradientKey === key && this.laneStripeGradients[0] !== null) {
      return;
    }
    this.laneGradientKey = key;
    for (let lane = 0; lane < MANIA_LANE_COUNT; lane += 1) {
      const baseColour = LANE_GEM_COLOURS[lane as ManiaLane];
      const accentColour = LANE_GEM_ACCENTS[lane as ManiaLane];
      const stripe = ctx.createLinearGradient(0, layout.highwayTopY, 0, layout.strumBarY);
      stripe.addColorStop(0, withAlpha(baseColour, 0.05));
      stripe.addColorStop(1, withAlpha(baseColour, 0.18));
      this.laneStripeGradients[lane] = stripe;

      const press = ctx.createLinearGradient(0, layout.highwayTopY, 0, layout.strumBarY);
      press.addColorStop(0, withAlpha(accentColour, 0));
      press.addColorStop(1, withAlpha(accentColour, 0.35));
      this.pressWashGradients[lane] = press;
    }
  }

  /**
   * Strum bar + per-lane fret caps at the foreground edge of the
   * highway. Fret caps are circular pads coloured by lane (GH-classic
   * palette: green / red / yellow / blue) so the player has an
   * always-visible reference for where each lane lives. The strum
   * bar itself is a thin coloured stripe whose hue follows the
   * combo tier — quiet white at low combos, warmer / more saturated
   * as the streak climbs.
   *
   * Optional `keyLabels` (one per lane) are drawn just below the
   * strum bar so a brand-new player can match physical keys to lanes
   * without flipping back to the tutorial. Empty labels are skipped.
   */
  drawStrumBar(
    ctx: CanvasRenderingContext2D,
    layout: GuitarLayout,
    palette: GuitarChart['palette'],
    combo: number,
    beat: number,
    pressedLanes: ReadonlySet<ManiaLane>,
    keyLabels: Record<ManiaLane, string>,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    const tier = comboTier(combo);
    const stripeColour =
      combo >= COMBO_BG_TIER_THRESHOLD ? COMBO_BLOOM_PALETTE[tier] : palette.padPerfect;
    const pulse = reducedMotion ? 0 : beatPulse(beat) * BEAT_PULSE_STRENGTH;

    ctx.save();
    if (!reducedMotion && quality.chromeShadows) {
      ctx.shadowColor = stripeColour;
      ctx.shadowBlur = 10 + Math.min(20, combo / 4) + pulse * 16;
    }
    ctx.fillStyle = withAlpha(stripeColour, Math.min(1, 0.85 + pulse * 0.15));
    ctx.fillRect(
      layout.centreX - layout.bottomStackWidth / 2,
      layout.strumBarY - 3,
      layout.bottomStackWidth,
      4,
    );
    ctx.fillStyle = withAlpha(stripeColour, 0.18 + pulse * 0.18);
    ctx.fillRect(
      layout.centreX - layout.bottomStackWidth / 2,
      layout.strumBarY - 18,
      layout.bottomStackWidth,
      36,
    );
    ctx.restore();

    // Fret caps — one per lane, just below the strum-bar centre line
    // so they read as "press this colour" markers. Pressed lanes pop
    // brighter; idle lanes show a darker base ring. Radius gets a
    // beat-locked breathing pulse so the row feels alive between gems.
    const stackLeft = layout.centreX - layout.bottomStackWidth / 2;
    const baseRadius = Math.min(layout.bottomLaneWidth * 0.32, 26);
    const radius = baseRadius * (1 + pulse * 0.08);
    for (let lane = 0; lane < MANIA_LANE_COUNT; lane += 1) {
      const laneCentreX =
        stackLeft +
        lane * (layout.bottomLaneWidth + LANE_GAP_PX) +
        layout.bottomLaneWidth / 2;
      const baseColour = LANE_GEM_COLOURS[lane as ManiaLane];
      const accentColour = LANE_GEM_ACCENTS[lane as ManiaLane];
      const isPressed = pressedLanes.has(lane as ManiaLane);

      ctx.save();
      if (!reducedMotion && quality.chromeShadows) {
        ctx.shadowColor = accentColour;
        ctx.shadowBlur = isPressed ? 18 + pulse * 10 : pulse * 8;
      }
      ctx.fillStyle = isPressed ? accentColour : withAlpha(baseColour, 0.65 + pulse * 0.2);
      ctx.beginPath();
      ctx.arc(laneCentreX, layout.strumBarY + 6, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = withAlpha('#ffffff', isPressed ? 0.85 : 0.35 + pulse * 0.2);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(laneCentreX, layout.strumBarY + 6, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      if (isPressed) {
        ctx.fillStyle = withAlpha('#ffffff', 0.7);
        ctx.beginPath();
        ctx.arc(
          laneCentreX - radius * 0.3,
          layout.strumBarY + 6 - radius * 0.3,
          radius * 0.22,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }

      const label = keyLabels[lane as ManiaLane];
      ctx.fillStyle = withAlpha('#ffffff', 0.85);
      ctx.font = `bold 12px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(label, laneCentreX, layout.strumBarY + baseRadius + 12);
    }
  }

  /**
   * Per-lane verdict flashes spawned by the FX system. Drawn as a
   * trapezoid wedge tinted toward the verdict colour, so the column
   * itself tells the player the outcome at peripheral vision. Lighter
   * than Piano's full-canvas-height flash because the highway is
   * narrower and a subtle wash reads cleaner against the perspective.
   */
  drawLaneFlashes(
    ctx: CanvasRenderingContext2D,
    laneFlashes: ReadonlyArray<PianoLaneFlash>,
    layout: GuitarLayout,
  ): void {
    if (laneFlashes.length === 0) {
      return;
    }
    const topGap = LANE_GAP_PX * (layout.topStackWidth / layout.bottomStackWidth);
    const topStackLeft = layout.centreX - layout.topStackWidth / 2;
    const bottomStackLeft = layout.centreX - layout.bottomStackWidth / 2;
    for (const flash of laneFlashes) {
      const t = flash.life / flash.maxLife;
      const a = flash.alpha * t * t;
      if (a <= 0.01) {
        continue;
      }
      const lane = flash.lane;
      const tlX = topStackLeft + lane * (layout.topLaneWidth + topGap);
      const trX = tlX + layout.topLaneWidth;
      const blX = bottomStackLeft + lane * (layout.bottomLaneWidth + LANE_GAP_PX);
      const brX = blX + layout.bottomLaneWidth;
      ctx.fillStyle = withAlpha(flash.colour, a);
      ctx.beginPath();
      ctx.moveTo(tlX, layout.highwayTopY);
      ctx.lineTo(trX, layout.highwayTopY);
      ctx.lineTo(brX, layout.strumBarY);
      ctx.lineTo(blX, layout.strumBarY);
      ctx.closePath();
      ctx.fill();
    }
  }
}

/**
 * Resolve the centre-x of a lane at the strum bar in CSS px. Used by
 * session FX (particle bursts, score-pops, shockwaves) so they
 * spawn from the visual centre of the lane the player just hit.
 * Wraps {@link computeLayout} so the session never duplicates the
 * geometry math.
 */
export function getLaneStrumCentre(
  canvasWidth: number,
  canvasHeight: number,
  lane: ManiaLane,
): { x: number; y: number } {
  const layout = computeLayout(canvasWidth, canvasHeight);
  const stackLeft = layout.centreX - layout.bottomStackWidth / 2;
  const x =
    stackLeft +
    lane * (layout.bottomLaneWidth + LANE_GAP_PX) +
    layout.bottomLaneWidth / 2;
  return { x, y: layout.strumBarY };
}
